import { useCallback, useEffect, useState } from 'react';
import { listCustomerSales, type CustomerSaleRow } from '../../infra/customer360Repo';

const PAGE_SIZE = 5;

export function useCustomerSales(customerId: string | null, visible: boolean) {
  const [sales, setSales] = useState<CustomerSaleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!visible || !customerId) {
      setSales([]);
      setPage(0);
      setHasMore(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPage(0);

    const load = async () => {
      try {
        const rows = await listCustomerSales(customerId, PAGE_SIZE, 0);
        if (!cancelled) {
          setSales(rows);
          setHasMore(rows.length === PAGE_SIZE);
        }
      } catch (err) {
        console.warn('[useCustomerSales] Error loading sales', err);
        if (!cancelled) {
          setSales([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [customerId, visible]);

  const loadPage = useCallback(
    async (newPage: number) => {
      if (!customerId) return;
      setLoading(true);
      try {
        const rows = await listCustomerSales(customerId, PAGE_SIZE, newPage * PAGE_SIZE);
        setSales(rows);
        setPage(newPage);
        setHasMore(rows.length === PAGE_SIZE);
      } catch (err) {
        console.warn('[useCustomerSales] Error loading page', err);
      } finally {
        setLoading(false);
      }
    },
    [customerId],
  );

  return { sales, loading, page, hasMore, pageSize: PAGE_SIZE, loadPage };
}
